<template>
  <div class="v-application--wrap">
    <CustomDialog />
    <GlobalLoading />
    <LogIn />
    <the-layout-toolbar @toggleDrawer="isDrawerOpen = !isDrawerOpen" />
    <the-layout-drawer
      v-if="$vuetify.breakpoint.sm || $vuetify.breakpoint.xs"
    />
    <the-layout-content />
    <!-- <the-layout-fab /> -->
    <the-layout-to-top-fab />
    <the-layout-footer />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TM from "@/api/ticket-management";
import TheLayoutToolbar from "./TheLayoutToolbar.vue";
import TheLayoutDrawer from "./TheLayoutDrawer.vue";
import TheLayoutContent from "./TheLayoutContent.vue";
import TheLayoutToTopFab from "./TheLayoutToTopFab.vue";
import TheLayoutFooter from "./TheLayoutFooter.vue";
import { CurrentReservedMixins } from "@/shared/mixins";
import LogIn from "./LogIn.vue";

let apiUrl = process.env.VUE_APP_API + process.env.VUE_APP_API_TM;
window.isPopState = false;
window.orderCanceled = false;

const popStateRoutes = [
  "Activity",
  "Order1",
  "ConfirmSt",
  "SelSt",
  "Order2",
  "Order3",
  "Done",
];

const isPopStateRoute = (route) =>
  route && popStateRoutes.indexOf(route.name) > -1;

const getSourceRoute = ($this) => {
  if (
    $this.prevRoute &&
    $this.prevRoute.name === "Order3" &&
    ["ConfirmSt", "SelSt", "Order2"].includes($this.currentRoute?.name)
  )
    return $this.currentRoute;
  if (isPopStateRoute($this.prevRoute)) return $this.prevRoute;
  if (isPopStateRoute($this.currentRoute)) return $this.currentRoute;
  return null;
};

const getRouteParams = (route) => ({
  eventId: route.params.eventId,
  sessionId: route.params.sessionId,
});

const pushRoute = ($this, name, params) => {
  const route = params ? { name, params } : { name };
  return $this.$router.push(route).catch(() => {});
};

const handlePopStateNavigation = async (e, $this) => {
  e.preventDefault();
  e.stopPropagation();

  if (window.isPopState) return;

  window.isPopState = true;

  await $this.$nextTick();

  window.prevRoute = $this.prevRoute;
  window.currentRoute = $this.currentRoute;
  window.$route = $this.$route;
  window.isSeated = $this.areaAssignment && $this.seatAssignment;
  const sourceRoute = getSourceRoute($this);

  if (!sourceRoute) {
    $this.initPopState();
    return;
  }

  if ($this.isGoback || $this.isPopState) {
    window.isPopState = false;
    return;
  }

  if (
    !$this.prevRoute ||
    (!isPopStateRoute($this.prevRoute) && !$this.currentRoute) ||
    (!isPopStateRoute($this.currentRoute) && !isPopStateRoute(sourceRoute))
  ) {
    $this.isGoback = false;
    $this.$forceUpdate();
  }

  const routeName = sourceRoute.name;
  const routeParams = getRouteParams(sourceRoute);

  $this.setPopState(true);
  document.body.classList.add("is-popstate");
  $this.isGoback = true;

  if (
    ["ConfirmSt", "SelSt"].includes(routeName) ||
    (routeName === "Order2" &&
      !window.isSeated &&
      window.prevRoute?.name !== "Order3")
  ) {
    $this.showDialog({
      title: $this.$t("layout_notification_title"),
      content: $this.$t("layout_notification_content"),
      buttons: [
        {
          text: $this.$t("layout_notification_cancel"),
          style: {
            block: true,
            outlined: true,
            color: "primary",
          },
          action: async () => {
            await pushRoute($this, routeName, routeParams);
            $this.initPopState();
          },
        },
        {
          text: $this.$t("layout_notification_confirm"),
          style: {
            depressed: true,
            block: true,
            outlined: false,
            color: "primary",
          },
          action: async () => {
            const { data } = await TM.ticketRelease({
              orderId: $this.reserveResponse.orderId,
              culturePointInfo: {},
            });
            if (data.errCode === "00") {
              $this.resetCulturePoint();
              $this.resetOrderState();
              $this.setPageData(null);
              await pushRoute($this, "Order1", routeParams);
              $this.initPopState();
            }
          },
        },
      ],
    });

    return;
  }

  switch (routeName) {
    case "Order3":
      if (window.isProcess) {
        alert($this.$t("leave_order"));
        await $this.cancelTicket($this, sourceRoute);
      } else {
        await pushRoute($this, "Order2", routeParams);
      }
      break;
    case "Order2":
      if (window.isProcess) {
        alert($this.$t("leave_order"));
        await $this.cancelTicket($this, sourceRoute);
      } else if (
        window.isSeated &&
        !window.isProcess &&
        (!window.prevRoute || window.prevRoute.name !== "Order3")
      ) {
        await pushRoute($this, "ConfirmSt", routeParams);
      }
      break;
    case "Order1":
      await pushRoute($this, "Activity", { id: routeParams.eventId });
      break;
    case "Done":
    case "Activity":
      await pushRoute($this, "Index");
      break;
    default:
      await pushRoute($this, "Order1", routeParams);
      break;
  }

  $this.initPopState();
  return false;
};

export default {
  name: "TheLayout",
  mixins: [CurrentReservedMixins],
  components: {
    TheLayoutToolbar,
    TheLayoutDrawer,
    TheLayoutContent,
    TheLayoutToTopFab,
    TheLayoutFooter,
    LogIn,
  },
  computed: {
    ...mapGetters({
      updateResponse: "order/updateResponse",
      reserveResponse: "order/reserveResponse",
      areaAssignment: "order/areaAssignment",
      seatAssignment: "order/seatAssignment",
      isPopState: "order/isPopState",
      campaign: "event/campaign",
    }),
  },
  data() {
    return {
      isGoback: false,
      currentRoute: "",
      prevRoute: "",
    };
  },
  watch: {
    $route: function ($route, oldRoute) {
      this.currentRoute = $route;
      this.prevRoute = oldRoute;
      this.$forceUpdate();
    },
  },
  beforeMount() {
    this.currentRoute = this.$route;
    window.addEventListener("beforeunload", this.confirmToReload);
    window.addEventListener("popstate", this.confirmToBack, false);
  },
  /**
  mounted() {
    const $this = this
    window.addEventListener("popstate", (e) => confirmToBack(e, $this), false);
  },
  */
  beforeDestroy() {
    window.removeEventListener("popstate", this.confirmToBack, false);
    window.removeEventListener("beforeunload", this.confirmToReload);
  },
  methods: {
    ...mapActions({
      showDialog: "dialog/show",
      resetOrderState: "order/resetOrderState",
      setPopState: "order/setPopState",
      setPageData: "order/setPageData",
      setTimer: "order/setTimer",
      reserveTicket: "order/reserveTicket",
      resetCulturePoint: "culturePoint/resetCulturePoint",
    }),
    async confirmToBack(event) {
      return handlePopStateNavigation(event, this);
    },
    async cancelTicket($this, sourceRoute) {
      const { data } = await TM.ticketRelease({
        orderId: $this.reserveResponse.orderId,
        culturePointInfo: {},
      });

      if (data.errCode === "00") {
        $this.resetCulturePoint();
        $this.resetOrderState();
        $this.setPageData(null);
        await pushRoute($this, "Order1", getRouteParams(sourceRoute));
      }
      window.orderCanceled = true;
      window.isProcess = false;
    },
    initPopState() {
      this.setPopState(false);
      this.isGoback = false;
      this.currentRoute = null;
      this.prevRoute = null;
      window.prevRoute = null;
      window.currentRoute = null;
      window.$route = null;
      document.body.classList.remove("is-popstate");
      window.isPopState = false;
      this.$forceUpdate();
    },
    confirmToReload(e) {
      let routes = ["ConfirmSt", "SelSt", "Order2", "Order3"];
      console.log("currentRoute", this.currentRoute);
      if (
        routes.indexOf(this.currentRoute.name) > -1 &&
        !window.isErrorToBack
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
  },
};
</script>
